const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const own = (record, key) => isRecord(record) ? Object.getOwnPropertyDescriptor(record, key)?.value : undefined;
const hasText = value => typeof value === 'string' && value.trim().length > 0;
const normalize = value => value.normalize('NFKC').toLocaleLowerCase('en-US').replace(/\s+/gu, '');

/** Build a fresh view of live learning data, with no browser, storage or network access. */
export function createLearningNotebook(parameters = {}) {
  const lessons = own(parameters, 'lessons'), courseModules = own(parameters, 'courseModules'), saved = own(parameters, 'saved');
  if (!Array.isArray(lessons) || !Array.isArray(courseModules)) return [];
  const modules = new Map();
  for (const module of courseModules) {
    const id = own(module, 'id'), label = own(module, 'label');
    if (hasText(id) && hasText(label) && !modules.has(id)) modules.set(id, label);
  }
  const validIds = value => new Set(Array.isArray(value) ? value.filter(hasText) : []);
  const bookmarks = validIds(own(saved, 'bookmarks')), completed = validIds(own(saved, 'completed'));
  const notes = own(saved, 'notes'), seen = new Set(), entries = [];
  for (const lesson of lessons) {
    const id = own(lesson, 'id'), title = own(lesson, 'title'), moduleId = own(lesson, 'moduleId');
    if (!hasText(id) || !hasText(title) || !modules.has(moduleId) || seen.has(id)) continue;
    seen.add(id);
    const note = own(notes, id);
    entries.push({ id, title, moduleLabel: modules.get(moduleId), note: typeof note === 'string' ? note.slice(0, 3000) : '', bookmarked: bookmarks.has(id), completed: completed.has(id) });
  }
  return entries;
}

function validEntries(entries) {
  if (!Array.isArray(entries)) return [];
  const seen = new Set();
  return entries.filter(entry => {
    const id = own(entry, 'id');
    if (!hasText(id) || seen.has(id) || !hasText(own(entry, 'title')) || !hasText(own(entry, 'moduleLabel'))
      || typeof own(entry, 'note') !== 'string' || typeof own(entry, 'bookmarked') !== 'boolean'
      || typeof own(entry, 'completed') !== 'boolean') return false;
    seen.add(id);
    return true;
  });
}

export function searchLearningNotebook(entries, query = '', view = 'notes') {
  if (typeof query !== 'string' || !['notes', 'saved'].includes(view)) return [];
  const needle = normalize(query);
  return validEntries(entries).filter(entry => (view === 'notes' ? hasText(entry.note) : entry.bookmarked)
    && (!needle || [entry.title, entry.moduleLabel, entry.note].some(value => normalize(value).includes(needle))));
}

function exportTimestamp(value) {
  if (value instanceof Date) {
    const time = Date.prototype.getTime.call(value);
    return Number.isFinite(time) ? new Date(time).toISOString() : null;
  }
  if (typeof value !== 'string') return null;
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(?:Z|[+-]\d{2}:\d{2})$/);
  if (!parts) return null;
  const [, year, month, day, hour, minute, second] = parts.map(Number);
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day); date.setUTCHours(hour, minute, second, 0);
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day
    || date.getUTCHours() !== hour || date.getUTCMinutes() !== minute || date.getUTCSeconds() !== second) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? new Date(time).toISOString() : null;
}

const escapeText = value => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/([\\`*_\[\]{}()#+.!|~-])/g, '\\$1');
const quote = value => escapeText(value.slice(0, 3000).trim()).replace(/\r\n?/g, '\n').split('\n').map(line => `> ${line}`).join('\n');
const inlineText = value => escapeText(value.replace(/\s+/gu, ' ').trim());

/** Export the current draft, regardless of persistence success; never include quizzes or other account data. */
export function formatLearningNotebook(entries, options = {}) {
  const valid = validEntries(entries), timestamp = exportTimestamp(options?.exportedAt);
  const sections = ['# 我的財經學習筆記'];
  if (timestamp) sections.push(`匯出時間：${timestamp}（UTC）`);
  sections.push('這份檔案是目前畫面持有的學習草稿，不代表投資建議。筆記與勾選狀態依本次資料匯出，未必已成功保存到瀏覽器。');
  const notes = valid.filter(entry => hasText(entry.note));
  sections.push('## 我的筆記', notes.length ? notes.map(entry => `### ${inlineText(entry.title)}\n\n單元：${inlineText(entry.moduleLabel)}\n\n${quote(entry.note)}`).join('\n\n') : '尚無筆記。');
  const completed = valid.filter(entry => entry.completed), bookmarked = valid.filter(entry => entry.bookmarked);
  sections.push('## 已完成的課程', completed.length ? completed.map(entry => `- ${inlineText(entry.title)}`).join('\n') : '尚未標記完成的課程。');
  sections.push('## 收藏的課程', bookmarked.length ? bookmarked.map(entry => `- ${inlineText(entry.title)}`).join('\n') : '尚無收藏課程。');
  return `${sections.join('\n\n')}\n`;
}
